import ResponsiveTable from '../../components/ResponsiveTable';
import React, { useState, useEffect } from 'react';
import CustomDateFields from '../../components/CustomDateFields';
import FilterScroller from '../../components/FilterScroller';
import Pagination from '../../components/Pagination';
import { useRecentSearches } from '../../components/RecentSearches';
import api from '../../utils/api'; 
import { isOutNow, attendanceStatusLabel } from '../../utils/attendanceStatus';
import { showAttendanceReasons } from '../../utils/attendanceReasons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faClock, faFilter, faSearch, faSignOutAlt, faSignInAlt, faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import '../../styles/App.css'; 

// A page holds a month at most: 31 days is the longest one.
const PAGE_SIZE = 31;

const Attendance = () => {
    // --- STATE ---
    const [currentTime, setCurrentTime] = useState(new Date());
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- FILTER STATE ---
    const [filterType, setFilterType] = useState('All');
    const [customDates, setCustomDates] = useState({ from: '', to: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const recentSearch = useRecentSearches('my-attendance', { value: searchTerm, onPick: setSearchTerm });
    const [currentPage, setCurrentPage] = useState(1);
    const [punchLoading, setPunchLoading] = useState(false);

    // A different filter or search is a different list: start at its first page.
    useEffect(() => {
        setCurrentPage(1);
    }, [filterType, searchTerm, customDates.from, customDates.to]);

    // --- USER DATA ---
    const user = JSON.parse(localStorage.getItem('user'));

    // --- INITIAL LOAD & CLOCK ---
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        fetchLogs(); 
        return () => clearInterval(timer);
    }, []);

    // --- API: FETCH LOGS ---
    const fetchLogs = async () => {
        try {
            const res = await api.get('/attendance/my-logs');
            setLogs(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching logs", err);
            setLoading(false);
        }
    };

    // --- API: WFH PUNCH ---
    const handlePunch = async (direction) => {
        try {
            setPunchLoading(true);
            await api.post('/attendance/wfh-punch', { direction });
            Swal.fire('Success', `Successfully punched ${direction}`, 'success');
            fetchLogs(); // refresh logs to get updated status
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Failed to punch', 'error');
        } finally {
            setPunchLoading(false);
        }
    };

    // --- API: WFH BREAK ---
    const handleBreak = async (action) => {
        try {
            setPunchLoading(true);
            await api.post('/attendance/wfh-break', { action });
            Swal.fire('Success', action === 'start' ? 'Break started! Take your time.' : 'Break ended. Welcome back!', 'success');
            fetchLogs();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Failed to update break', 'error');
        } finally {
            setPunchLoading(false);
        }
    };

    // --- HELPER: CHECK SHORT LEAVE ELIGIBILITY ---
    const isShortLeaveEligible = (log) => {
        if (log.status !== 'Half Day') return false;
        if (!log.checkIn) return false;

        // Late plus early minutes, worked out by the server on every punch.
        // Same 2-hour limit as SHORT_LEAVE_MAX_MINUTES in server/utils/attendancePunch.js.
        if (log.missedMinutes != null) return log.missedMinutes <= 120;

        // If checked out, they must have worked at least 6.33 hours (since 8.33 is Present)
        // 8.33 - 2 hours = 6.33 hours minimum for a 2-hour short leave
        if (log.checkOut) {
            return (log.totalHours || 0) >= 6.33;
        }

        // If not checked out yet, check if they arrived <= 2 hours late
        const inTime = new Date(log.checkIn);
        // The shift that day was worked on, which for night staff called in
        // during the day is not their profile shift.
        const shift = log.shiftType || user?.shiftType;
        const shiftStartHour = shift === 'NIGHT' ? 19 : 9;
        const shiftStartMin = shift === 'NIGHT' ? 0 : 30;
        
        const shiftStartObj = new Date(inTime.getFullYear(), inTime.getMonth(), inTime.getDate(), shiftStartHour, shiftStartMin, 0, 0);
        
        const lateMs = inTime.getTime() - shiftStartObj.getTime();
        const lateHours = lateMs / 3600000;
        
        return lateHours <= 2.0 && lateHours >= 0; 
    };

    // --- API: SHORT LEAVE ---
    const handleShortLeave = async (logId) => {
        const { value: reason } = await Swal.fire({
            title: 'Apply for Short Leave',
            input: 'text',
            inputLabel: 'Reason for short leave (e.g. Train delayed, Left early for doctor)',
            inputPlaceholder: 'Enter reason...',
            showCancelButton: true,
            confirmButtonColor: '#215D7B',
            inputValidator: (value) => {
                if (!value) return 'You need to write something!'
            }
        });

        if (reason) {
            try {
                setPunchLoading(true);
                await api.post(`/attendance/short-leave/${logId}`, { reason });
                Swal.fire('Submitted', 'Your short leave request has been submitted for approval.', 'success');
                fetchLogs();
            } catch (error) {
                Swal.fire('Error', error.response?.data?.message || 'Failed to submit request', 'error');
            } finally {
                setPunchLoading(false);
            }
        }
    };

    // --- API: REASON FOR A LATE / HALF DAY ---
    // The employee's own explanation, kept apart from the system note (which
    // every punch rewrites) and from the short leave reason.
    const canAddReason = (log) => log.status === 'Late' || log.status === 'Half Day';

    const handleReason = async (log) => {
        recentSearch.save();
        const what = log.status === 'Late' ? 'arriving late' : 'the half day';
        const result = await Swal.fire({
            title: log.employeeNote ? 'Your reason' : 'Add a reason',
            input: 'textarea',
            inputLabel: `Reason for ${what} on ${log.date}`,
            inputPlaceholder: 'e.g. Train delayed, doctor appointment…',
            inputValue: log.employeeNote || '',
            inputAttributes: { maxlength: 300, 'aria-label': `Reason for ${what}` },
            showCancelButton: true,
            confirmButtonText: 'Save',
            confirmButtonColor: '#215D7B',
            inputValidator: (value) => {
                if (!value.trim() && !log.employeeNote) return 'Write a short reason first.';
            }
        });
        if (!result.isConfirmed) return;

        try {
            setPunchLoading(true);
            await api.put(`/attendance/reason/${log._id}`, { reason: result.value.trim() });
            fetchLogs();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Could not save your reason', 'error');
        } finally {
            setPunchLoading(false);
        }
    };

    // --- HELPER: PARSE DD/MM/YYYY TO JS DATE ---
    const parseDateStr = (dateStr) => {
        if (!dateStr) return new Date();
        const [d, m, y] = dateStr.split('/');
        return new Date(y, m - 1, d);
    };

    // --- HELPER: CALCULATE WORKING HOURS ---
    const calculateDuration = (start, end, status) => {
        // 👇 Handle Absent / On Leave / Ongoing specifically
        if (status === 'Absent' || status === 'On Leave') return "0h 0m";
        if (!start || !end) return <span className="text-muted italic">0h 0m (Ongoing)</span>;
        
        const startTime = new Date(start);
        const endTime = new Date(end);
        const diffMs = endTime - startTime;
        
        if (diffMs < 0) return "-";

        const totalMinutes = Math.floor(diffMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return `${hours}h ${minutes}m`;
    };

    // --- HELPER: FORMAT BREAK TIME ---
    const formatBreakTime = (minutes) => {
        if (!minutes || minutes <= 0) return "-";
        
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        
        if (h > 0) return `${h}h ${m}m`;
        return `${m}m`;
    };

    // --- FILTER & SORT LOGIC ---
    const filteredLogs = logs
        .filter(log => {
            // 1. Search Filter (Search by Date, Status, or Note)
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = 
                log.date.toLowerCase().includes(searchLower) || 
                log.status.toLowerCase().includes(searchLower) || 
                (log.note && log.note.toLowerCase().includes(searchLower)) ||
                (log.employeeNote && log.employeeNote.toLowerCase().includes(searchLower)) ||
                (log.shortLeaveReason && log.shortLeaveReason.toLowerCase().includes(searchLower));
            
            if (!matchesSearch) return false;

            // 2. Date Filter
            if (filterType === 'All') return true;

            const logDate = parseDateStr(log.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (filterType === 'Today') {
                return logDate.getTime() === today.getTime();
            }
            if (filterType === 'Week') {
                const lastWeek = new Date(today);
                lastWeek.setDate(today.getDate() - 7);
                return logDate >= lastWeek && logDate <= today;
            }
            if (filterType === 'Month') {
                return logDate.getMonth() === today.getMonth() && logDate.getFullYear() === today.getFullYear();
            }
            if (filterType === 'Custom') {
                if (customDates.from && customDates.to) {
                    const fromDate = new Date(customDates.from);
                    fromDate.setHours(0, 0, 0, 0);
                    const toDate = new Date(customDates.to);
                    toDate.setHours(23, 59, 59, 999);
                    return logDate >= fromDate && logDate <= toDate;
                }
                return true; 
            }

            return true;
        })
        // 👇 Sort by Date Descending (Newest first)
        .sort((a, b) => parseDateStr(b.date) - parseDateStr(a.date));

    // --- PAGINATION (31 a page) ---
    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
    // Clamped: a punch or a refresh can shorten the list under the page being viewed.
    const page = Math.min(currentPage, totalPages);
    const pageLogs = filteredLogs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    if (loading) return <div className="main-content">Loading Attendance...</div>;

    const today = new Date();
    const todayStr = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;
    const todayLog = logs.find(log => log.date === todayStr);
    
    // Simple state checking: if they have a checkIn but no checkOut, they are working.
    // If they have both, they are done for the day (checked out).
    const isPunchedIn = todayLog && todayLog.checkIn && !todayLog.checkOut;
    const isCheckedOut = todayLog && todayLog.checkOut;
    const isOnBreak = todayLog && todayLog.isOnBreak;

    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const shortLeavesUsedThisMonth = logs.filter(log => {
        if (!log.date) return false;
        const [, m, y] = log.date.split('/');
        return parseInt(m) === currentMonth && parseInt(y) === currentYear && (log.shortLeaveStatus === 'Pending' || log.shortLeaveStatus === 'Approved');
    }).length;
    const hasReachedLimit = shortLeavesUsedThisMonth >= 2;

    return (
        <div className="attendance-container">
            
            {/* Header with Clock */}
            <div className="attendance-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                <div className="attendance-heading" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <h2 className="page-title header-no-margin">My Attendance</h2>
                    <span className="attendance-mobile-date">{currentTime.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <div className="digital-clock clock-light">
                        <FontAwesomeIcon icon={faClock} className="clock-icon" />
                        {currentTime.toLocaleTimeString()}
                    </div>
                </div>

                {/* Remote Manual Punch Widget (Available to all) */}
                <div className="wfh-punch-widget" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {!isPunchedIn && !isCheckedOut && (
                        <button 
                            className="gts-btn success" 
                            onClick={() => handlePunch('IN')}
                            disabled={punchLoading}
                        >
                            <FontAwesomeIcon icon={faSignInAlt} /> Check In
                        </button>
                    )}
                    {isPunchedIn && !isOnBreak && (
                        <button 
                            className="gts-btn wfh-break-btn" 
                            onClick={() => handleBreak('start')}
                            disabled={punchLoading}
                        >
                            <FontAwesomeIcon icon={faPause} /> Take Break
                        </button>
                    )}
                    {isPunchedIn && isOnBreak && (
                        <button 
                            className="gts-btn wfh-resume-btn" 
                            onClick={() => handleBreak('end')}
                            disabled={punchLoading}
                        >
                            <FontAwesomeIcon icon={faPlay} /> End Break
                        </button>
                    )}
                    {isPunchedIn && (
                        <button 
                            className="gts-btn danger" 
                            onClick={() => handlePunch('OUT')}
                            disabled={punchLoading}
                        >
                            <FontAwesomeIcon icon={faSignOutAlt} /> Check Out
                        </button>
                    )}
                    {isCheckedOut && (
                        <span className="status-badge success">Checked out for today</span>
                    )}
                </div>
            </div>

            {/* FILTER CONTROLS */}
            <div className="control-card filter-bar-card">
                
                {/* 1. Filter Buttons */}
                <FilterScroller rowClassName="button-group">
                    {['Today', 'Week', 'Month', 'All', 'Custom'].map(type => (
                        <button
                            key={type}
                            className={`gts-btn filter-btn ${filterType === type ? 'primary active' : 'warning inactive'}`}
                            onClick={() => setFilterType(type)}
                        >
                            {type === 'Custom' && <FontAwesomeIcon icon={faFilter} className="filter-icon" />}
                            {type}
                        </button>
                    ))}
                </FilterScroller>

                {/* 2. Custom Date Inputs (Conditional) */}
                {filterType === 'Custom' && <CustomDateFields value={customDates} onChange={setCustomDates} />}

                {/* 3. Search Bar */}
                <div className="search-wrapper">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                        {...recentSearch.inputProps}
                        type="text"
                        placeholder="Search records..."
                        className="swal2-input search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {recentSearch.menu}
                </div>
            </div>

            {/* LOGS TABLE */}
            <div className="employee-table-container fade-in">
                <h3 className="table-header-title">
                    <FontAwesomeIcon icon={faHistory} className="table-header-icon" /> Activity Log
                </h3>
                <ResponsiveTable className="employee-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Time In</th>
                            <th>Time Out</th>
                            <th>Work Hours</th>
                            <th>Break Time</th>
                            <th>Status</th>
                            <th>Note</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLogs.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="empty-table-message">
                                    No records match your filters.
                                </td>
                            </tr>
                        ) : (
                            pageLogs.map((log, index) => (
                                <tr key={log._id || index}>
                                    <td data-label="Date" className="fw-600 text-dark-gray">{log.date}</td>
                                    <td data-label="Time In">
                                        {log.checkIn ? new Date(log.checkIn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}
                                    </td>
                                    <td data-label="Time Out">
                                        {log.checkOut ? new Date(log.checkOut).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}
                                    </td>
                                    
                                    <td data-label="Work Hours" className="fw-bold text-primary">
                                        {/* 👇 Pass status to logic */}
                                        {calculateDuration(log.checkIn, log.checkOut, log.status)}
                                    </td>

                                    <td data-label="Break Time" className="fw-bold text-orange">
                                        {formatBreakTime(log.breakTimeTaken)}
                                    </td>

                                    <td data-label="Status">
                                        <span className={`status-badge ${
                                            isOutNow(log) ? 'primary' :
                                            log.status === 'Absent' ? 'danger' :
                                            log.status === 'On Leave' ? 'primary' :
                                            (log.status === 'Half Day' || log.status === 'Late') ? 'warning' : 
                                            'success'
                                        }`}>
                                            {attendanceStatusLabel(log)}
                                        </span>
                                    </td>
                                    <td data-label="Note" className="text-small text-muted">
                                        {log.note || '-'}
                                    </td>
                                    <td data-label="Action">
                                        <div className="att-action-stack">
                                        {isShortLeaveEligible(log) && (!log.shortLeaveStatus || log.shortLeaveStatus === 'None') ? (
                                            <button 
                                                style={{
                                                    background: hasReachedLimit ? 'light-dark(#f1f5f9, var(--theme-surface-raised, #292c31))' : 'transparent',
                                                    border: `1px solid ${hasReachedLimit ? 'light-dark(#cbd5e1, var(--theme-border-strong, #4a4e55))' : '#0284c7'}`,
                                                    color: hasReachedLimit ? 'light-dark(#94a3b8, var(--theme-subtle, #7a808a))' : 'light-dark(#0284c7, var(--theme-accent-ink, #78c2e5))',
                                                    padding: '2px 8px',
                                                    fontSize: '0.7rem',
                                                    borderRadius: '4px',
                                                    cursor: hasReachedLimit ? 'not-allowed' : 'pointer',
                                                    fontWeight: '500',
                                                    transition: 'all 0.2s'
                                                }}
                                                title={hasReachedLimit ? "You have reached the limit of 2 short leaves this month." : ""}
                                                onMouseOver={(e) => { if(!hasReachedLimit) e.target.style.background = '#e0f2fe'; }}
                                                onMouseOut={(e) => { if(!hasReachedLimit) e.target.style.background = 'transparent'; }}
                                                onClick={() => !hasReachedLimit && handleShortLeave(log._id)}
                                                disabled={hasReachedLimit}
                                            >
                                                Apply Short Leave
                                            </button>
                                        ) : ['Pending', 'Approved', 'Rejected'].includes(log.shortLeaveStatus) ? (
                                            // Tapping the status shows the reason sent with the request.
                                            <button
                                                type="button"
                                                className={`att-status-link text-small fw-bold ${log.shortLeaveStatus.toLowerCase()}`}
                                                onClick={() => showAttendanceReasons({ ...log, employeeNote: '' })}
                                                disabled={!log.shortLeaveReason}
                                                title={log.shortLeaveReason ? 'View the reason you gave' : undefined}
                                            >
                                                Short Leave {log.shortLeaveStatus}
                                            </button>
                                        ) : (
                                            !canAddReason(log) && <span className="text-small text-muted" style={{ fontStyle: 'italic' }}>No action needed</span>
                                        )}
                                        {canAddReason(log) && (
                                            <button
                                                type="button"
                                                className={`att-text-link ${log.employeeNote ? 'has-reason' : ''}`}
                                                onClick={() => handleReason(log)}
                                                disabled={punchLoading}
                                                title={log.employeeNote ? 'View or change your reason' : `Tell your manager why this day is a ${log.status}`}
                                            >
                                                {log.employeeNote ? 'View reason' : 'Add reason'}
                                            </button>
                                        )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </ResponsiveTable>
            </div>

            {/* Only once the list runs past one page ("All", or a long custom range). */}
            {!loading && totalPages > 1 && (
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalRecords={filteredLogs.length}
                    limit={PAGE_SIZE}
                    onPageChange={setCurrentPage}
                />
            )}

        </div>
    );
};

export default Attendance;
